// ============================================================================
// The Validate window — confirm the date, set the new rates, review. Item #706.
//
// Ben, 2026-10-05/08 (Decisions 1A, 2A, 3A; approved demo
// https://claude.ai/artifact/PxvAZuWfeiNmVdcfqfKPjg): Validate now SETS the
// rate. Each service line's new rate goes through record_rate_change, the one
// door (TCSD-0188), from inside validate_kicker(facility, date, lines, ...);
// the account card follows by the recompute trigger. Nothing on this screen
// writes a table.
//
// ⛔ THE RULE DATE AND THE NEXT INCREASE COME FROM THE DATABASE —
// annual_increase_date() and annual_increase_next_date(). This file asks; it
// does not compute (TCSD-0341). lib/annual-increase.test.ts fails if it starts.
//
// ⛔ A FAILED LOAD NEVER LEAVES A FORM THAT CAN SAVE. If the lines or the dates
// do not load, the window says so and offers no Validate button: saving blanks
// over a real rate is the discard pattern STATE warns about.
//
// "It already happened" (Decision 1A): when Jose types a new rate equal to the
// line's current rate, the window asks what it was before, pre-filled from the
// last change on record, and records history only. If that change is already on
// record the database refuses and names the record (one source of truth, Ben
// 2026-10-08) — the window shows its sentence.
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '../../shared/components';
import { supabase } from '../../shared/supabase';
import { rpcErrorMessage } from '../../shared/rpc-error';
import { overlayClickClose } from '../../shared/overlay-close';
import {
  anniversaryNear,
  fmtLongDate,
  fmtMoney2,
  fmtShortDate,
  isoDate,
  lastChangeFor,
  pctChange,
  roundRate,
  ROUNDING_LABELS,
  withIncrease,
  type AuditChange,
  type LastChange,
  type RateRecord,
  type Rounding,
} from './lib/annual-increase';

export interface WindowBuilding {
  facility_id: string;
  facility_name: string | null;
  account_name: string | null;
  contract_start: string | null;
  next_kicker_date: string;
  annual_kicker_pct: number | null;
  kicker_seed_pending: boolean;
  monthly_rate: number | null;
}

export type WindowMode =
  | { kind: 'validate' }
  | { kind: 'finish'; kv_id: string; kicker_date: string; acted_on: string };

interface Line {
  assignment_id: string;
  label: string;
  billing_method: string | null;
  billing_rate: number;
  cleaner: string | null;
  last: LastChange | null;
}

interface SavedChange {
  change_id: string;
  status: 'Applied' | 'Scheduled';
  effective_date: string;
  from: number | null;
  to: number;
  assignment_id: string;
  history_only: boolean;
}

interface SaveResult {
  kv_id: string;
  start_date: string;
  next_increase_date: string;
  next_increase_basis: string;
  monthly_rate: number | null;
  changes: SavedChange[];
}

const STEP_LABELS = ['Confirm date', 'Set new rates', 'Review'];

function todayIso(): string {
  return isoDate(new Date());
}

// PostgREST returns a to-one embed as an object; the generated types say array.
function catalogName(sc: unknown): string | null {
  const row = (Array.isArray(sc) ? sc[0] : sc) as { display_name?: string | null } | null | undefined;
  return row?.display_name ?? null;
}

function methodLabel(m: string | null): string {
  if (!m) return 'Billing method not set';
  return m.replace(/_/g, ' ');
}

export function AnnualIncreaseWindow({
  building,
  mode,
  onClose,
  onDone,
}: {
  building: WindowBuilding;
  mode: WindowMode;
  onClose: () => void;
  onDone: () => void;
}) {
  const [step, setStep] = useState(1);
  const [lines, setLines] = useState<Line[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [ruleDate, setRuleDate] = useState<string | null>(null);
  const [dateChoice, setDateChoice] = useState<'rule' | 'stored'>('rule');
  const [newRates, setNewRates] = useState<Record<string, string>>({});
  const [prevRates, setPrevRates] = useState<Record<string, string>>({});
  const [rounding, setRounding] = useState<Rounding>('exact');
  const [note, setNote] = useState('');
  const [nextDate, setNextDate] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [result, setResult] = useState<SaveResult | null>(null);

  const pct = building.annual_kicker_pct;
  // Finish mode: the increase was validated against its anniversary; Validate
  // mode: the stored date on the queue.
  const around = mode.kind === 'finish' ? mode.kicker_date : building.next_kicker_date;
  const anniversary = anniversaryNear(building.contract_start, around);

  // ---- load: lines, their last change on record, and the rule date --------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: sa, error: e1 } = await supabase
          .from('service_assignments')
          .select('assignment_id, provider_id, service_code, service_catalog(display_name), billing_method, billing_rate, assignment_status, worker_name')
          .eq('facility_id', building.facility_id)
          .eq('assignment_status', 'Active');
        if (e1) throw e1;
        const rated = (sa ?? []).filter((l) => l.billing_rate != null);
        const ids = rated.map((l) => l.assignment_id);
        const providerIds = Array.from(new Set(rated.map((l) => l.provider_id).filter(Boolean))) as string[];
        const [rcp, audit, providers, rule] = await Promise.all([
          supabase
            .from('rate_change_proposals')
            .select('proposal_id, target_table, target_row_id, field_name, current_value, proposed_value, status, effective_date, created_at, kv_id, reason')
            .eq('facility_id', building.facility_id),
          ids.length
            ? supabase
                .from('audit_log')
                .select('record_id, changed_at, old_data, new_data, changed_fields')
                .eq('table_name', 'service_assignments')
                .in('record_id', ids)
                .contains('changed_fields', ['billing_rate'])
                .order('changed_at', { ascending: false })
                .limit(50)
            : Promise.resolve({ data: [], error: null }),
          providerIds.length
            ? supabase.from('service_providers').select('provider_id, partner_name').in('provider_id', providerIds)
            : Promise.resolve({ data: [], error: null }),
          anniversary
            ? supabase.rpc('annual_increase_date', { p_anniversary: anniversary })
            : Promise.resolve({ data: null, error: null }),
        ]);
        if (rcp.error) throw rcp.error;
        if (audit.error) throw audit.error;
        if (providers.error) throw providers.error;
        if (rule.error) throw rule.error;
        const audits: AuditChange[] = ((audit.data ?? []) as Array<{ record_id: string; changed_at: string; old_data: Record<string, unknown> | null; new_data: Record<string, unknown> | null }>).map((a) => ({
          record_id: a.record_id,
          changed_at: a.changed_at,
          old_rate: a.old_data?.billing_rate == null ? null : Number(a.old_data.billing_rate),
          new_rate: a.new_data?.billing_rate == null ? null : Number(a.new_data.billing_rate),
        }));
        const names = new Map(((providers.data ?? []) as Array<{ provider_id: string; partner_name: string | null }>).map((p) => [p.provider_id, p.partner_name]));
        const records = (rcp.data ?? []) as unknown as RateRecord[];
        const built: Line[] = rated.map((l) => ({
          assignment_id: l.assignment_id,
          label: catalogName(l.service_catalog) ?? l.service_code ?? 'Service line',
          billing_method: l.billing_method,
          billing_rate: Number(l.billing_rate),
          // The line's own cleaner name first, the provider's name only where it is
          // blank — the same answer the onboarding card gives (PLAYBOOK §10d3).
          cleaner: l.worker_name ?? (l.provider_id ? names.get(l.provider_id) : null) ?? null,
          last: lastChangeFor(l.assignment_id, rated.length, records, audits),
        }));
        if (cancelled) return;
        setLines(built);
        setRuleDate((rule.data as string | null) ?? null);
        setNewRates(Object.fromEntries(built.map((l) => [l.assignment_id, withIncrease(l.billing_rate, pct).toFixed(2)])));
      } catch (err) {
        if (!cancelled) setLoadError(rpcErrorMessage(err, 'The service lines did not load.'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [building.facility_id, anniversary, pct]);

  const storedDiffers = mode.kind === 'validate' && ruleDate != null && ruleDate !== building.next_kicker_date;
  const startDate: string | null =
    mode.kind === 'finish'
      ? ruleDate ?? mode.kicker_date
      : dateChoice === 'stored' || ruleDate == null
        ? building.next_kicker_date
        : ruleDate;
  const hasPassed = startDate != null && startDate <= todayIso();

  // The next increase for the review step, from the database.
  useEffect(() => {
    if (step !== 3 || !startDate || !building.contract_start) return;
    const contractStart = building.contract_start;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.rpc('annual_increase_next_date', {
        p_contract_start: contractStart,
        p_start_date: startDate,
      });
      if (cancelled) return;
      setNextDate(error ? null : ((data as string | null) ?? null));
    })();
    return () => {
      cancelled = true;
    };
  }, [step, startDate, building.contract_start]);

  // ---- step 2 arithmetic --------------------------------------------------
  const parsed = useMemo(() => {
    const out: Record<string, number | null> = {};
    for (const l of lines ?? []) {
      const v = Number(String(newRates[l.assignment_id] ?? '').replace(/[^0-9.]/g, ''));
      out[l.assignment_id] = newRates[l.assignment_id] === '' || !Number.isFinite(v) ? null : v;
    }
    return out;
  }, [lines, newRates]);

  const sameAsNow = (l: Line) => parsed[l.assignment_id] != null && parsed[l.assignment_id] === l.billing_rate;
  // A line typed equal to today's rate is "it already happened" only when the
  // rate is a real figure; a $0 line that stays $0 is simply not sent.
  const needsBefore = (l: Line) => sameAsNow(l) && l.billing_rate > 0;
  const prevFor = (l: Line) => {
    const v = Number(prevRates[l.assignment_id]);
    return prevRates[l.assignment_id] && Number.isFinite(v) && v >= 0 ? v : null;
  };

  const payload = useMemo(
    () =>
      (lines ?? [])
        .filter((l) => parsed[l.assignment_id] != null)
        .filter((l) => !sameAsNow(l) || (needsBefore(l) && prevFor(l) != null))
        .map((l) => ({
          assignment_id: l.assignment_id,
          new_rate: parsed[l.assignment_id],
          previous_rate: sameAsNow(l) ? prevFor(l) : null,
        })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lines, parsed, prevRates],
  );

  const step2Ok =
    lines != null &&
    lines.every((l) => parsed[l.assignment_id] != null && (parsed[l.assignment_id] as number) >= 0) &&
    lines.every((l) => !needsBefore(l) || prevFor(l) != null) &&
    payload.length > 0;

  const currentCard = (lines ?? []).reduce((s, l) => s + l.billing_rate, 0);
  const newCard = (lines ?? []).reduce((s, l) => s + (parsed[l.assignment_id] ?? l.billing_rate), 0);

  function applyRounding(r: Rounding) {
    setRounding(r);
    setNewRates(Object.fromEntries((lines ?? []).map((l) => [l.assignment_id, roundRate(l.billing_rate * (1 + (pct ?? 0)), r).toFixed(2)])));
  }

  function openStep2() {
    // Pre-fill "the rate before" from the last change on record wherever it
    // ends at today's rate.
    setPrevRates((p) => {
      const next = { ...p };
      for (const l of lines ?? []) {
        if (next[l.assignment_id] == null && l.last && l.last.to === l.billing_rate && l.last.from != null) {
          next[l.assignment_id] = l.last.from.toFixed(2);
        }
      }
      return next;
    });
    setStep(2);
  }

  async function save() {
    if (!startDate) return;
    setBusy(true);
    setSaveError(null);
    const { data, error } = await supabase.rpc('validate_kicker', {
      p_facility_id: building.facility_id,
      p_start_date: startDate,
      p_lines: payload,
      p_notes: note.trim() || undefined,
      p_kv_id: mode.kind === 'finish' ? mode.kv_id : undefined,
    });
    if (error) {
      setSaveError(rpcErrorMessage(error, 'Nothing was saved. Try again, and if it persists tell the COO.'));
      setBusy(false);
      return;
    }
    setResult(data as unknown as SaveResult);
    setBusy(false);
    setStep(4);
  }

  const cleaner = lines?.find((l) => l.cleaner)?.cleaner ?? null;
  const title = (
    <>
      {building.facility_name ?? building.facility_id}
      {cleaner && <span className="font-normal text-tcs-text-secondary"> ({cleaner})</span>}
    </>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-tcs-scrim/60 sm:items-center sm:p-4"
      {...overlayClickClose(busy ? () => undefined : step === 4 ? onDone : onClose)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="max-h-[100dvh] w-full overflow-y-auto rounded-t-xl bg-tcs-overlay shadow-xl sm:max-w-[760px] sm:rounded-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ai-window-title"
      >
        <div className="border-b border-tcs-border px-5 pb-3 pt-4">
          <p className="text-xs text-tcs-text-muted">
            {mode.kind === 'finish'
              ? `Set the rate for an increase validated ${fmtLongDate(mode.acted_on)}`
              : 'Validate annual increase'}
          </p>
          <h3 id="ai-window-title" className="text-base font-semibold text-tcs-text">
            {title}
          </h3>
          <p className="text-xs text-tcs-text-muted">
            {building.account_name ?? '—'} · {building.facility_id}
          </p>
          {step < 4 && (
            <ol className="mt-3 flex gap-2" aria-label="Steps">
              {STEP_LABELS.map((s, i) => (
                <li
                  key={s}
                  aria-current={i + 1 === step ? 'step' : undefined}
                  className={`flex flex-1 items-center gap-2 text-xs ${i + 1 === step ? 'font-medium text-tcs-text' : 'text-tcs-text-muted'}`}
                >
                  <span
                    className={`grid h-5 w-5 flex-none place-items-center rounded-full border text-[11px] ${
                      i + 1 < step ? 'border-tcs-text bg-tcs-text text-tcs-overlay' : i + 1 === step ? 'border-tcs-text' : 'border-tcs-border'
                    }`}
                  >
                    {i + 1 < step ? '✓' : i + 1}
                  </span>
                  <span className="hidden sm:inline">{s}</span>
                </li>
              ))}
            </ol>
          )}
        </div>

        {loadError ? (
          <div className="px-5 py-5">
            <p className="rounded-md bg-st-danger-bg px-3 py-2 text-sm text-st-danger-fg-strong" role="alert">
              {loadError} Nothing can be saved until it loads — close the window and try again.
            </p>
            <div className="mt-4 flex justify-end">
              <Button variant="ghost" size="sm" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        ) : lines == null ? (
          <div className="flex items-center gap-2 px-5 py-8 text-sm text-tcs-text-secondary">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading the service lines…
          </div>
        ) : lines.length === 0 ? (
          <div className="px-5 py-5 text-sm text-tcs-text-secondary">
            This building has no active service line with a client rate, so there is nothing to increase. The
            account manager adds the line first.
            <div className="mt-4 flex justify-end">
              <Button variant="ghost" size="sm" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        ) : step === 1 ? (
          <StepDate
            building={building}
            anniversary={anniversary}
            ruleDate={ruleDate}
            storedDiffers={storedDiffers}
            dateChoice={dateChoice}
            setDateChoice={setDateChoice}
            startDate={startDate}
            hasPassed={hasPassed}
            seeded={building.kicker_seed_pending}
            onCancel={onClose}
            onNext={openStep2}
          />
        ) : step === 2 ? (
          <div>
            <div className="px-5 py-4">
              <p className="text-sm text-tcs-text">
                The current rate on each service line and what a {((pct ?? 0) * 100).toFixed(1)}% increase looks like.
                Save it as shown or type the rate you want. Starts <b>{fmtLongDate(startDate)}</b>.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-tcs-text-secondary">
                Round new rates
                <div role="group" aria-label="Rounding" className="inline-flex overflow-hidden rounded-md border border-tcs-border">
                  {(Object.keys(ROUNDING_LABELS) as Rounding[]).map((r) => (
                    <button
                      key={r}
                      type="button"
                      aria-pressed={rounding === r}
                      onClick={() => applyRounding(r)}
                      className={`border-r border-tcs-border px-2.5 py-1 last:border-r-0 ${rounding === r ? 'bg-tcs-text text-tcs-overlay' : 'bg-tcs-raised'}`}
                    >
                      {ROUNDING_LABELS[r]}
                    </button>
                  ))}
                </div>
              </div>
              <ul className="mt-3 space-y-2">
                {lines.map((l) => {
                  const v = parsed[l.assignment_id];
                  const ch = v == null ? null : pctChange(l.billing_rate, v);
                  const off = ch != null && pct != null && Math.abs(ch - pct * 100) > 0.5;
                  return (
                    <li key={l.assignment_id} className="rounded-md border border-tcs-border bg-tcs-raised p-3">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-tcs-text">{l.label}</div>
                          <div className="text-xs text-tcs-text-muted">
                            {l.assignment_id} · {methodLabel(l.billing_method)}
                          </div>
                          <LastChangeLine last={l.last} />
                        </div>
                        <div className="grid grid-cols-[auto_auto] items-center gap-x-3 gap-y-1 text-sm">
                          <span className="text-xs text-tcs-text-muted">Current</span>
                          <span className="text-right tabular-nums">{fmtMoney2(l.billing_rate)}</span>
                          <span className="text-xs text-tcs-text-muted">With {((pct ?? 0) * 100).toFixed(0)}%</span>
                          <span className="text-right tabular-nums">{fmtMoney2(withIncrease(l.billing_rate, pct))}</span>
                          <label htmlFor={`nr-${l.assignment_id}`} className="text-xs font-medium text-tcs-text">
                            New rate
                          </label>
                          <span className="inline-flex items-center rounded-md border border-tcs-border bg-tcs-field px-2">
                            <span className="text-tcs-text-muted">$</span>
                            <input
                              id={`nr-${l.assignment_id}`}
                              inputMode="decimal"
                              value={newRates[l.assignment_id] ?? ''}
                              onChange={(e) => setNewRates((p) => ({ ...p, [l.assignment_id]: e.target.value }))}
                              className="w-24 bg-transparent py-1.5 text-right tabular-nums focus:outline-none"
                            />
                          </span>
                          <span className="text-xs text-tcs-text-muted">Change</span>
                          <span className={`text-right text-xs tabular-nums ${off ? 'font-medium text-st-warn-fg-strong' : 'text-tcs-text-secondary'}`}>
                            {ch == null ? '—' : `${ch >= 0 ? '+' : ''}${ch.toFixed(2)}%`}
                          </span>
                        </div>
                      </div>
                      {needsBefore(l) && (
                        <div className="mt-3 rounded-md bg-st-warn-bg px-3 py-2 text-xs text-tcs-text">
                          <label htmlFor={`pr-${l.assignment_id}`} className="block font-medium">
                            This is today's rate. If the increase already happened, what was the rate before it?
                          </label>
                          <span className="mt-1 inline-flex items-center rounded-md border border-tcs-border bg-tcs-field px-2">
                            <span className="text-tcs-text-muted">$</span>
                            <input
                              id={`pr-${l.assignment_id}`}
                              inputMode="decimal"
                              value={prevRates[l.assignment_id] ?? ''}
                              onChange={(e) => setPrevRates((p) => ({ ...p, [l.assignment_id]: e.target.value }))}
                              className="w-24 bg-transparent py-1 text-right tabular-nums focus:outline-none"
                            />
                          </span>
                          <span className="ml-2 text-tcs-text-secondary">Recorded as history only — the line is not changed.</span>
                        </div>
                      )}
                      {sameAsNow(l) && l.billing_rate === 0 && (
                        <p className="mt-2 text-xs text-tcs-text-muted">Stays at $0 — nothing is recorded for this line.</p>
                      )}
                    </li>
                  );
                })}
              </ul>
              <div className="mt-3 flex items-center justify-between rounded-md bg-tcs-bg-muted px-3 py-2 text-sm">
                <span>Monthly rate on the account card</span>
                <span className="tabular-nums">
                  {fmtMoney2(currentCard)} → <b>{fmtMoney2(Math.round(newCard * 100) / 100)}</b>
                </span>
              </div>
              <label className="mt-3 block text-xs text-tcs-text-secondary">
                Note (optional)
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  placeholder="For example: rounded to match the client's PO"
                  className="mt-1 block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
                />
              </label>
              {!step2Ok && payload.length === 0 && lines.every((l) => parsed[l.assignment_id] != null) && (
                <p className="mt-2 text-xs text-st-warn-fg-strong">Nothing changes yet — set at least one new rate.</p>
              )}
            </div>
            <div className="flex flex-wrap justify-between gap-2 border-t border-tcs-border px-5 py-3">
              <Button variant="ghost" size="sm" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button variant="primary" size="sm" onClick={() => setStep(3)} disabled={!step2Ok}>
                Review
              </Button>
            </div>
          </div>
        ) : step === 3 ? (
          <div>
            <div className="px-5 py-4 text-sm text-tcs-text">
              <p className="mb-2">When you {mode.kind === 'finish' ? 'save' : 'validate'}:</p>
              <ol className="list-decimal space-y-2 pl-5">
                {payload.map((p) => {
                  const l = lines.find((x) => x.assignment_id === p.assignment_id) as Line;
                  return p.previous_rate != null ? (
                    <li key={p.assignment_id}>
                      {l.label}: recorded that it already went from <b>{fmtMoney2(p.previous_rate)}</b> to{' '}
                      <b>{fmtMoney2(p.new_rate)}</b> on {fmtLongDate(startDate)}. The line is not changed.
                    </li>
                  ) : (
                    <li key={p.assignment_id}>
                      {l.label} goes from <b>{fmtMoney2(l.billing_rate)}</b> to <b>{fmtMoney2(p.new_rate)}</b>{' '}
                      {hasPassed ? 'today, back-dated to' : 'on'} {fmtLongDate(startDate)}.
                    </li>
                  );
                })}
                <li>
                  {payload.every((p) => p.previous_rate != null)
                    ? `The account card stays at ${fmtMoney2(currentCard)}.`
                    : hasPassed
                      ? `The account card changes now to ${fmtMoney2(Math.round(newCard * 100) / 100)}.`
                      : `The account card stays at ${fmtMoney2(currentCard)} until ${fmtLongDate(startDate)}, showing the scheduled increase, then changes to ${fmtMoney2(Math.round(newCard * 100) / 100)}.`}
                </li>
                <li>
                  Each rate change is logged with the reason <b>Annual increase</b>
                  {mode.kind === 'finish' ? ' and linked to the increase validated earlier' : ''}
                  {note.trim() ? ', with your note' : ''}.
                </li>
                <li>
                  Next annual increase:{' '}
                  <b>
                    {nextDate
                      ? fmtLongDate(nextDate)
                      : building.contract_start
                        ? '…'
                        : `one year after ${fmtLongDate(startDate)} (no contract start on file)`}
                  </b>
                  {nextDate ? ' — the next contract anniversary, by the rule.' : ''}
                </li>
              </ol>
              {hasPassed && payload.some((p) => p.previous_rate == null) && (
                <p className="mt-3 rounded-md bg-st-warn-bg px-3 py-2 text-xs">
                  <b>Invoices already sent at the old rate are not changed.</b> If a month was billed at the old rate,
                  that difference is handled outside this step.
                </p>
              )}
              {saveError && (
                <p className="mt-3 rounded-md bg-st-danger-bg px-3 py-2 text-xs text-st-danger-fg-strong" role="alert">
                  {saveError}
                </p>
              )}
            </div>
            <div className="flex flex-wrap justify-between gap-2 border-t border-tcs-border px-5 py-3">
              <Button variant="ghost" size="sm" onClick={() => setStep(2)} disabled={busy}>
                Back
              </Button>
              <Button variant="primary" size="sm" onClick={save} disabled={busy}>
                {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {mode.kind === 'finish' ? 'Save rate' : 'Validate increase'}
              </Button>
            </div>
          </div>
        ) : result ? (
          <Done result={result} lines={lines} currentCard={currentCard} note={note} onDone={onDone} finish={mode.kind === 'finish'} />
        ) : null}
      </div>
    </div>
  );
}

function LastChangeLine({ last }: { last: LastChange | null }) {
  if (!last) return <p className="mt-1 text-xs text-tcs-text-muted">No earlier rate change on record.</p>;
  const ch = pctChange(last.from, last.to);
  const when = last.on ? `on ${fmtLongDate(last.on)}` : `recorded ${fmtLongDate(last.recordedOn)} (no start date was kept)`;
  const where =
    last.source === 'outside'
      ? ' — outside the app'
      : last.source === 'building'
        ? " — on the building's rate"
        : '';
  return (
    <p className="mt-1 text-xs text-tcs-text-secondary">
      Last change {ch == null ? `to ${fmtMoney2(last.to)}` : `${ch >= 0 ? '+' : ''}${ch.toFixed(1)}%`} {when}
      {where}
      {last.annualIncrease && (
        <span className="ml-1 rounded bg-tcs-bg-muted px-1 py-0.5 text-[10px] font-semibold">Annual increase</span>
      )}
    </p>
  );
}

function StepDate({
  building,
  anniversary,
  ruleDate,
  storedDiffers,
  dateChoice,
  setDateChoice,
  startDate,
  hasPassed,
  seeded,
  onCancel,
  onNext,
}: {
  building: WindowBuilding;
  anniversary: string | null;
  ruleDate: string | null;
  storedDiffers: boolean;
  dateChoice: 'rule' | 'stored';
  setDateChoice: (c: 'rule' | 'stored') => void;
  startDate: string | null;
  hasPassed: boolean;
  seeded: boolean;
  onCancel: () => void;
  onNext: () => void;
}) {
  return (
    <div>
      <div className="px-5 py-4">
        <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
          <div>
            <dt className="text-xs text-tcs-text-muted">Contract start</dt>
            <dd className="font-medium">{fmtLongDate(building.contract_start)}</dd>
          </div>
          <div>
            <dt className="text-xs text-tcs-text-muted">Anniversary this year</dt>
            <dd className="font-medium">{fmtLongDate(anniversary)}</dd>
          </div>
          <div>
            <dt className="text-xs text-tcs-text-muted">Annual increase</dt>
            <dd className="font-medium">{((building.annual_kicker_pct ?? 0) * 100).toFixed(1)}%</dd>
          </div>
        </dl>
        <p className="mt-3 rounded-md bg-tcs-bg-muted px-3 py-2 text-sm text-tcs-text-secondary">
          {anniversary && ruleDate
            ? `An anniversary in the first seven days of a month starts the increase on the 1st of that month; any later day starts it on the 1st of the next. This one, ${fmtShortDate(anniversary)}, gives ${fmtLongDate(ruleDate)}. No proration.`
            : 'There is no contract start on file, so the rule cannot give a date. The date on the queue is used.'}
        </p>
        {seeded && (
          <p className="mt-2 text-xs text-tcs-text-secondary">
            This date was estimated from the contract start and has not been confirmed before.
          </p>
        )}
        <p className="mt-3 text-xs text-tcs-text-muted">New rate takes effect</p>
        <p className="text-2xl font-semibold text-tcs-text">{fmtLongDate(startDate)}</p>
        {storedDiffers && (
          <fieldset className="mt-3 space-y-2">
            <legend className="mb-1 text-xs text-tcs-text-secondary">
              The date on the queue does not match the rule. Pick the one to use.
            </legend>
            <label className="flex cursor-pointer items-start gap-2 rounded-md border border-tcs-border p-2 text-sm">
              <input type="radio" name="ai-date" checked={dateChoice === 'rule'} onChange={() => setDateChoice('rule')} className="mt-1" />
              <span>
                <span className="font-medium">{fmtLongDate(ruleDate)}</span>
                <span className="block text-xs text-tcs-text-secondary">From the anniversary rule.</span>
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-2 rounded-md border border-tcs-border p-2 text-sm">
              <input type="radio" name="ai-date" checked={dateChoice === 'stored'} onChange={() => setDateChoice('stored')} className="mt-1" />
              <span>
                <span className="font-medium">{fmtLongDate(building.next_kicker_date)}</span>
                <span className="block text-xs text-tcs-text-secondary">The date on the queue today (a deferral or an earlier entry).</span>
              </span>
            </label>
          </fieldset>
        )}
        {hasPassed && (
          <p className="mt-3 rounded-md bg-st-warn-bg px-3 py-2 text-sm">
            <b>This date has passed.</b> The new rate applies as soon as you validate. Invoices already sent at the old
            rate are not changed.
          </p>
        )}
      </div>
      <div className="flex flex-wrap justify-between gap-2 border-t border-tcs-border px-5 py-3">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="primary" size="sm" onClick={onNext} disabled={!startDate}>
          Confirm date
        </Button>
      </div>
    </div>
  );
}

function Done({
  result,
  lines,
  currentCard,
  note,
  onDone,
  finish,
}: {
  result: SaveResult;
  lines: Line[];
  currentCard: number;
  note: string;
  onDone: () => void;
  finish: boolean;
}) {
  const applied = result.changes.some((c) => c.status === 'Applied' && !c.history_only);
  const scheduled = result.changes.filter((c) => c.status === 'Scheduled');
  const scheduledTotal =
    scheduled.length > 0
      ? Math.round((currentCard + scheduled.reduce((s, c) => s + (c.to - (c.from ?? 0)), 0)) * 100) / 100
      : null;
  return (
    <div>
      <div className="px-5 py-4">
        <p className="text-sm font-semibold text-st-ok-fg-strong">{finish ? 'Rate saved' : 'Increase validated'}</p>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="rounded-md border border-tcs-border p-3">
            <p className="text-xs font-semibold text-tcs-text-secondary">Account card</p>
            <p className="mt-1 text-xs text-tcs-text-muted">Monthly rate</p>
            <p className="text-xl font-semibold tabular-nums">{fmtMoney2(result.monthly_rate)}</p>
            {applied && <p className="text-xs text-tcs-text-muted">Changed by the annual increase, back-dated to {fmtLongDate(result.start_date)}.</p>}
            {scheduledTotal != null && (
              <p className="mt-1 inline-block rounded-full bg-st-info-bg px-2 py-0.5 text-xs text-st-info-fg-strong">
                Increase scheduled {fmtShortDate(scheduled[0].effective_date)}: {fmtMoney2(scheduledTotal)}
              </p>
            )}
            <p className="mt-2 text-xs text-tcs-text-muted">Next annual increase {fmtLongDate(result.next_increase_date)}</p>
          </div>
          <div className="rounded-md border border-tcs-border p-3">
            <p className="text-xs font-semibold text-tcs-text-secondary">Rate history</p>
            <ul className="mt-2 space-y-2">
              {result.changes.map((c) => (
                <li key={c.change_id} className="border-l-2 border-tcs-text pl-2 text-sm">
                  {lines.find((l) => l.assignment_id === c.assignment_id)?.label ?? c.assignment_id}:{' '}
                  {fmtMoney2(c.from)} → {fmtMoney2(c.to)}{' '}
                  <span className="rounded bg-tcs-bg-muted px-1 py-0.5 text-[10px] font-semibold">Annual increase</span>
                  <span className="block text-xs text-tcs-text-muted">
                    {c.change_id} · {c.history_only ? 'Recorded as already happened' : c.status} for {fmtLongDate(c.effective_date)}
                    {note.trim() ? ` · “${note.trim()}”` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <div className="flex justify-end border-t border-tcs-border px-5 py-3">
        <Button variant="primary" size="sm" onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  );
}
